import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateVN, formatDateVNFromTs, formatVND, toMoney } from '@rs/contracts'
import { Badge, Button, Card, Modal } from '@rs/ui'
import { qk, useList, useOne } from '../../shared/queries.ts'
import { useAction } from '../../shared/action.ts'
import { fetchMyLegs } from './api.ts'
import {
  ackInvoiceApi, fetchMyApInvoices, fetchMyRecon, sendReconApi, type MyApInvoice,
} from './recon-api.ts'

/**
 * ══ CỔNG THẦU PHỤ ▸ ĐỐI SOÁT (F21) ════════════════════════════════════════════════════════════
 * Chép `NccPortal.tsx` tab `recon`.
 *
 * ⭐ LẦN ĐẦU CỔNG THẦU PHỤ HIỆN SỐ TIỀN. Tab "Việc của tôi" (F13) cố ý KHÔNG có ô tiền nào — ở đó
 *   là việc của người khác. Ở đây là CÔNG NỢ CỦA CHÍNH HỌ, nên tiền là nội dung chính.
 *
 * ⚠ HAI VIỆC KHÁC HẲN NHAU trên cùng một tab, đừng trộn:
 *   ① XÁC NHẬN một chứng từ RT đã ghi sổ — một chiều, không rút lại được;
 *   ② GỬI ĐỀ XUẤT của mình khi thấy lệch — gửi bao nhiêu lần cũng được, mỗi lần một dấu vết.
 */

const ST_TINT: Record<string, string> = {
  posted: 'bg-slate-100 text-slate-600',
  partial: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-rose-100 text-rose-700',
}

interface Draft { label: string; amount: string }

export const ReconTab = (): JSX.Element => {
  const { t } = useTranslation()
  /* `useAction` (soát 16/10, T1): lỗi gửi đề xuất hiện TRONG hộp đề xuất (trước đây ở băng đầu trang,
     bị lớp phủ che — thầu phụ bấm Gửi mà không thấy gì); cờ bận chặn gửi hai đề xuất (L30). */
  const action = useAction()
  const err = action.err
  const [open, setOpen] = useState(false)
  /**
   * ⭐ TICK ĐƠN, KHÔNG GÕ MÃ. Bắt thầu phụ gõ tay "RS-20260912-0001, RS-20260912-0002" là mời sai
   *   một ký tự — và khi sai thì đề xuất trỏ vào một đơn không tồn tại, kế toán bên này phải hỏi
   *   lại từ đầu. Danh sách chỉ gồm chặng CỦA HỌ, đúng thứ họ có quyền đối soát.
   */
  const [pickedRefs, setPickedRefs] = useState<ReadonlySet<string>>(new Set())
  const [note, setNote] = useState('')
  const [lines, setLines] = useState<Draft[]>([{ label: '', amount: '' }])

  // ⚠ `useOne` chứ không `useList`: cửa này trả MỘT đối tượng (danh sách + ba con số tổng), không
  //   phải một trang. Bọc nó vào `useList` là nói dối lớp truy vấn về hình dạng dữ liệu.
  const { data: page, err: invErr, reload } = useOne(qk.myApInvoices(), fetchMyApInvoices)
  const { rows: mine, reload: reloadMine } = useList(qk.myRecon(), fetchMyRecon)

  /* Chặng CỦA CHÍNH THẦU PHỤ NÀY — máy chủ lấy `vendorId` từ phiên, cửa không nhận tham số đó.
     ⭐ CHỈ ĐƠN ĐÃ NHẬN (T13, soát 16/10): bản cũ `myConfirmedJobs` lọc `vendorConfirmed` (`NccPortal.tsx:292-295`) — chặng
     chưa nhận hay đã TỪ CHỐI không phải chuyến họ chạy, khai cước cho nó là lời khai rác. Máy chủ chặn y luật này. */
  const { rows: myLegs } = useList(qk.myLegs(), fetchMyLegs)
  const legOfRef = new Map((myLegs ?? []).filter((l) => l.vendorConfirmed && !l.vendorRejected).map((l) => [l.jobRef, l]))
  const myRefs = [...legOfRef.keys()].sort()

  const invoices = page?.rows ?? []
  const sum = page?.summary ?? null

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    const r = await action.run(fn)
    if (!r.ok) return
    setOpen(false); setPickedRefs(new Set()); setNote(''); setLines([{ label: '', amount: '' }])
    reload(); reloadMine()
  }
  const close = (): void => { setOpen(false); action.setErr(null) }

  /* Bản cũ `submitStmtReq` (`NccPortal.tsx:329-333`): chỉ lấy khoản CÓ tên VÀ số tiền > 0; chưa chọn đơn ⇒ "Chọn ít nhất
     1 đơn.", chưa có khoản ⇒ "Nhập ít nhất 1 khoản…" — báo ngay trong hộp, không gọi máy chủ. */
  const goodLines = lines.map((l) => ({ label: l.label.trim(), amount: l.amount.trim() }))
    .filter((l) => l.label !== '' && Number(l.amount) > 0)
  const submit = (): void => {
    if (pickedRefs.size === 0) { action.setErr(t('ncc.stmtReq.needJob')); return }
    if (goodLines.length === 0) { action.setErr(t('ncc.stmtReq.needItem')); return }
    void run(() => sendReconApi({
      jobRefs: myRefs.filter((r) => pickedRefs.has(r)),
      items: goodLines,
      ...(note.trim() === '' ? {} : { note: note.trim() }),
    }))
  }

  const setLine = (i: number, patch: Partial<Draft>): void => {
    setLines((cur) => cur.map((l, k) => (k === i ? { ...l, ...patch } : l)))
  }

  const allPicked = myRefs.length > 0 && myRefs.every((r) => pickedRefs.has(r))

  return (
    <div>
      {((open ? null : err) ?? invErr) !== null && (
        <div role="alert" className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {(open ? null : err) ?? invErr}
        </div>
      )}

      {sum !== null && (
        <div className="flex divide-x divide-slate-100 rounded-xl border border-slate-200 bg-white p-3 mb-3 text-center">
          <div className="flex-1 px-2">
            <div className="text-[10px] text-slate-400 uppercase font-bold">{t('ncc.apTotal')}</div>
            <div className="font-bold text-slate-800 tabular-nums">{formatVND(toMoney(sum.invoiceTotal))} đ</div>
          </div>
          <div className="flex-1 px-2">
            <div className="text-[10px] text-slate-400 uppercase font-bold">{t('ncc.apPaid')}</div>
            <div className="font-bold text-emerald-600 tabular-nums">{formatVND(toMoney(sum.paidTotal))} đ</div>
          </div>
          <div className="flex-1 px-2">
            <div className="text-[10px] text-slate-400 uppercase font-bold">{t('ncc.apDue')}</div>
            <div className="font-bold text-red-600 tabular-nums">{formatVND(toMoney(sum.dueTotal))} đ</div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {invoices.length === 0 && <Card className="p-6 text-center text-slate-400 italic text-sm">{t('ncc.emptyAps')}</Card>}
        {invoices.map((a: MyApInvoice) => {
          // Mã đơn trên các dòng cước — bản cũ hiện `📦 azJobRef` cạnh số chứng từ.
          const refs = [...new Set(a.lines.map((l) => l.jobRef).filter((x): x is string => x !== null && x !== ''))]
          return (
            <Card key={a.id} className="p-3.5">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-slate-700">{a.docNo}</span>
                    {refs.map((r) => <Badge key={r} className="bg-violet-100 text-violet-700">📦 {r}</Badge>)}
                    <Badge className={ST_TINT[a.status] ?? ''}>
                      {a.status === 'paid' ? t('ncc.apPaidFull')
                        : a.status === 'partial' ? t('ncc.apPartial') : t('ncc.apUnpaid')}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{formatDateVN(a.docDate)} · {a.lines[0]?.label ?? t('ncc.freight')}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-slate-800 tabular-nums">{formatVND(toMoney(a.total))} đ</div>
                  <div className="text-[10px] text-slate-400">{t('ncc.inclVat')}</div>
                </div>
              </div>
              {/* Chi tiết dòng cước — giá cước của CHÍNH họ (bản cũ `NccPortal.tsx:973`). */}
              {a.lines.length > 0 && (
                <div className="mt-2 rounded-lg bg-slate-50 border border-slate-100 divide-y divide-slate-100 text-xs">
                  {a.lines.map((l, i) => (
                    <div key={i} className="flex items-center justify-between px-2.5 py-1.5">
                      <span className="text-slate-600 truncate">{l.label}</span>
                      <span className="tabular-nums font-semibold text-slate-700 whitespace-nowrap">
                        {formatVND(toMoney(l.amount))} <span className="text-slate-400 font-normal">+VAT {l.vatRate}%</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 flex items-center justify-end gap-2">
                {a.vendorAckAt !== null
                  ? <span className="text-[11px] font-bold text-emerald-700">{t('ncc.ackDone', { date: formatDateVNFromTs(a.vendorAckAt) })}</span>
                  : a.status === 'cancelled'
                    ? null
                    : (
                      <Button size="sm" variant="ghost" onClick={() => { void run(() => ackInvoiceApi(a.id, a.version)) }}>
                        {t('ncc.ackBtn')}
                      </Button>
                      )}
              </div>
            </Card>
          )
        })}
      </div>

      {/* ⚠ BỎ `ncc.reconNote` của bản cũ, CÓ LÝ DO: nội dung nó ("sai lệch xin liên hệ kế toán") là cách
          làm TRƯỚC F21 — nay họ có đúng một khối ngay bên dưới để nói việc đó. */}

      {/* ── ĐỀ XUẤT CỦA THẦU PHỤ — KHÔNG thay thế bảng kê chính thức ─────────────────────── */}
      <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/40 p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs font-bold text-violet-800">📝 {t('ncc.stmtReq.title')}</div>
          <Button size="sm" disabled={myRefs.length === 0} onClick={() => { setOpen(true) }}>{t('ncc.stmtReq.btnNew')}</Button>
        </div>
        <p className="text-[10.5px] text-violet-600 mt-1">{t('ncc.stmtReq.hint')}</p>

        {(mine ?? []).length > 0 && (
          <div className="mt-2 space-y-1.5">
            {(mine ?? []).map((r) => (
              <div key={r.id} className="rounded-lg bg-white border border-violet-100 px-2.5 py-1.5 text-xs">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <span className="font-mono text-slate-500">{formatDateVNFromTs(r.createdAt)}</span>{' '}
                    <span className="text-slate-600">{r.jobRefs.join(', ')}</span>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span className="font-bold text-slate-800 tabular-nums">{formatVND(toMoney(r.total))} đ</span>
                    {r.status === 'reviewed'
                      ? <Badge className="bg-emerald-100 text-emerald-700">{t('ncc.stmtReq.stReviewed')}</Badge>
                      : <Badge className="bg-amber-100 text-amber-700">{t('ncc.stmtReq.stPending')}</Badge>}
                  </div>
                </div>
                {r.replyNote !== null && (
                  <div className="text-[11px] text-sky-700 mt-1">{t('ncc.stmtReq.replyFrom')} {r.replyNote}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ĐỀ XUẤT bảng kê đối soát cước — chọn đơn + tự kê khoản/tiền (bản cũ user 18/08, `NccPortal.tsx:363`). */}
      {open && (
        <Modal title={`📝 ${t('ncc.stmtReq.title')}`} onClose={close} className="max-w-lg" error={err}>
          <p className="text-[11px] text-slate-500 mb-2">{t('ncc.stmtReq.formHint')}</p>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] font-semibold text-slate-500">{t('ncc.stmtReq.pickJobs')}</div>
            <button type="button" className="text-[11px] font-semibold text-brand-600 hover:underline"
                    onClick={() => { setPickedRefs(allPicked ? new Set() : new Set(myRefs)) }}>
              {allPicked ? t('ncc.stmtReq.unselectAllJobs') : t('ncc.stmtReq.selectAllJobs')}
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 mb-3">
            {myRefs.map((ref) => {
              const lg = legOfRef.get(ref)
              return (
                <label key={ref} className="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={pickedRefs.has(ref)} aria-label={ref}
                         onChange={() => {
                           setPickedRefs((prev) => {
                             const next = new Set(prev)
                             if (next.has(ref)) next.delete(ref)
                             else next.add(ref)
                             return next
                           })
                         }} />
                  <span className="font-mono font-bold text-violet-600">{ref}</span>
                  <span className="text-slate-500 truncate flex-1 min-w-0">{lg?.jobCargo ?? lg?.jobContNo ?? ''}</span>
                </label>
              )
            })}
          </div>
          <div className="text-[11px] font-semibold text-slate-500 mb-1">{t('ncc.stmtReq.items')}</div>
          <div className="space-y-1.5 mb-2">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <input value={l.label} aria-label={t('ncc.stmtReq.itemLabelPh')} placeholder={t('ncc.stmtReq.itemLabelPh')}
                       onChange={(e) => { setLine(i, { label: e.target.value }) }}
                       className="flex-1 border border-slate-300 rounded-lg px-2 py-1.5 text-[12px]" />
                <input value={l.amount} inputMode="numeric" aria-label={t('ncc.stmtReq.itemAmountPh')} placeholder={t('ncc.stmtReq.itemAmountPh')}
                       onChange={(e) => { setLine(i, { amount: e.target.value.replace(/\D/g, '') }) }}
                       className="w-28 border border-slate-300 rounded-lg px-2 py-1.5 text-[12px] text-right tabular-nums" />
                {lines.length > 1 && (
                  <button type="button" className="text-red-400 hover:text-red-600 px-1"
                          onClick={() => { setLines(lines.filter((_, k) => k !== i)) }}>🗑</button>
                )}
              </div>
            ))}
            <button type="button" className="text-[11px] font-semibold text-brand-600 hover:underline"
                    onClick={() => { setLines([...lines, { label: '', amount: '' }]) }}>＋ {t('ncc.stmtReq.addItem')}</button>
          </div>
          <textarea value={note} rows={2} aria-label={t('ncc.stmtReq.notePh')} placeholder={t('ncc.stmtReq.notePh')}
                    onChange={(e) => { setNote(e.target.value) }}
                    className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-[12px] w-full mb-3" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>{t('ncc.cancel')}</Button>
            <Button disabled={action.busy} onClick={submit}>
              {t('ncc.stmtReq.submit')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
